import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { structureAPI } from '../../services/api';
import { useLabDemo } from '../../context/LabDemoContext';
import { LabRecommendedBadge, LabIndicatorDot } from '../../components/LabBadges';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Network, List, Building2, Settings, Search, MapPin, Users, Sparkles, Cog, TrendingUp, FlaskConical, AlertTriangle } from 'lucide-react';
import { Input } from '../../components/ui/input';
import OrgChart from './OrgChart';
import RegionalChart from './RegionalChart';
import DirectoryTab from './DirectoryTab';
import TeamDirectoryTab from './TeamDirectoryTab';
import DepartmentsTab from './DepartmentsTab';
import SettingsTab from './SettingsTab';
import MarketingDepartmentView from './MarketingDepartmentView';
import OperationsDepartmentView from './OperationsDepartmentView';
import ExpansionSimulatorTab from './ExpansionSimulatorTab';
import { DESIGN } from '../../components/DesignSystem';

const REGIONS = [
  { id: 'region_1', label: 'Region 1' },
  { id: 'region_2', label: 'Region 2' },
  { id: 'region_3', label: 'Region 3' },
  { id: 'region_4', label: 'Region 4' },
  { id: 'region_5', label: 'Region 5' },
];

const FUNCTIONAL_DEPTS = [
  { id: 'marketing', label: 'Marketing' },
  { id: 'operations', label: 'Operations' },
];

const Structure = () => {
  const { labTeamRoles, demoCompleted, conversionDate } = useLabDemo();
  const [activeTab, setActiveTab] = useState('national');
  const [selectedRegion, setSelectedRegion] = useState('region_1');
  const [selectedFunctionalDept, setSelectedFunctionalDept] = useState('marketing');
  const [orgTree, setOrgTree] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [treeRes, rolesRes, deptsRes, peopleRes] = await Promise.all([
        structureAPI.getOrgTree(),
        structureAPI.getRoles({ status: 'all' }),
        structureAPI.getDepartments(),
        structureAPI.getPeople()
      ]);
      
      setOrgTree(treeRes.data.tree || []);
      setRoles(rolesRes.data.roles || []);
      setDepartments(deptsRes.data.departments || []);
      setPeople(peopleRes.data.people || []);
    } catch (error) {
      console.error('Error fetching structure data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRoleUpdate = async () => {
    await fetchData();
  };

  // Tab configurations
  const tabs = [
    { id: 'national', label: 'National', icon: Network },
    { id: 'regions', label: 'Regions', icon: MapPin },
    { id: 'unique-ip', label: 'Unique IP', icon: Sparkles },
    { id: 'functional-depts', label: 'Functional Depts', icon: Cog, accent: '#A855F7' },
    { id: 'roles', label: 'Roles', icon: List },
    { id: 'internal-team', label: 'Internal Team', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'expansion', label: 'Expansion Sim', icon: TrendingUp, accent: '#6366F1' }
  ];

  return (
    <div className="space-y-8" data-testid="structure-page">
      {/* Premium Page Header */}
      <div className="relative">
        <div 
          className="absolute inset-0 rounded-3xl opacity-30"
          style={{
            background: 'linear-gradient(135deg, rgba(30, 58, 95, 0.1) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(59, 130, 246, 0.05) 100%)'
          }}
        />
        <div className="relative flex items-center justify-between py-6">
          <div className="flex items-center gap-4">
            <div 
              className="w-14 h-14 rounded-2xl flex items-center justify-center"
              style={{ 
                background: 'linear-gradient(135deg, #1E3A5F 0%, #2C5282 100%)',
                boxShadow: '0 0 25px rgba(30, 58, 95, 0.4)'
              }}
            >
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-black tracking-tight" style={{ color: DESIGN.colors.textPrimary }}>
                STRUCTURE
              </h1>
              <p className="text-base" style={{ color: DESIGN.colors.textSecondary }}>
                Organizational architecture and role management
              </p>
            </div>
          </div>
          
          {/* Premium Search Input */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: DESIGN.colors.textMuted }} />
            <Input
              placeholder="Search roles, people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 w-72 h-12 rounded-xl border-0 shadow-lg"
              style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}
              data-testid="structure-search"
            />
          </div>
        </div>
      </div>

      {/* Premium Tab Navigation */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div 
          className="p-2 rounded-2xl"
          style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.lg }}
        >
          <TabsList className="flex flex-wrap gap-2 bg-transparent h-auto p-0">
            {tabs.map(tab => (
              <TabsTrigger 
                key={tab.id}
                value={tab.id} 
                className="flex items-center gap-2 px-5 py-3 rounded-xl data-[state=active]:shadow-md transition-all"
                style={{ 
                  borderLeft: tab.accent ? `3px solid ${tab.accent}` : 'none'
                }}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon 
                  className="w-4 h-4" 
                  style={{ color: tab.accent || 'currentColor' }} 
                />
                <span className="font-semibold">{tab.label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {/* Tab Contents */}
        <div className="mt-6">
          <TabsContent value="national">
            <OrgChart 
              tree={orgTree} 
              loading={loading} 
              departments={departments}
              people={people}
              onUpdate={handleRoleUpdate}
              searchQuery={searchQuery}
              scopeFilter="national"
            />
          </TabsContent>

          <TabsContent value="regions">
            <div className="mb-6">
              <div 
                className="inline-flex items-center gap-4 p-4 rounded-2xl"
                style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
              >
                <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textSecondary }}>
                  Select Region:
                </span>
                <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                  <SelectTrigger 
                    className="w-[200px] rounded-xl border-0"
                    style={{ backgroundColor: DESIGN.colors.background }}
                    data-testid="region-selector"
                  >
                    <SelectValue placeholder="Select a region" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGIONS.map((region) => (
                      <SelectItem key={region.id} value={region.id}>
                        {region.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <RegionalChart 
              regionId={selectedRegion}
              regionLabel={REGIONS.find(r => r.id === selectedRegion)?.label || 'Region'}
              departments={departments}
              people={people}
              onUpdate={handleRoleUpdate}
            />
          </TabsContent>

          <TabsContent value="unique-ip">
            <OrgChart 
              tree={orgTree} 
              loading={loading} 
              departments={departments}
              people={people}
              onUpdate={handleRoleUpdate}
              searchQuery={searchQuery}
              scopeFilter="unique_ip"
            />
          </TabsContent>

          <TabsContent value="functional-depts">
            <div className="mb-6">
              <div 
                className="inline-flex items-center gap-4 p-4 rounded-2xl"
                style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
              >
                <span className="text-sm font-semibold" style={{ color: DESIGN.colors.textSecondary }}>
                  Select Department:
                </span>
                <Select value={selectedFunctionalDept} onValueChange={setSelectedFunctionalDept}>
                  <SelectTrigger 
                    className="w-[200px] rounded-xl border-0"
                    style={{ backgroundColor: DESIGN.colors.background }}
                    data-testid="functional-dept-selector"
                  >
                    <SelectValue placeholder="Select department" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUNCTIONAL_DEPTS.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>
                        {dept.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {selectedFunctionalDept === 'marketing' && <MarketingDepartmentView />}
            {selectedFunctionalDept === 'operations' && <OperationsDepartmentView />}
          </TabsContent>

          <TabsContent value="roles">
            {/* LAB Recommended Roles Section */}
            {demoCompleted && labTeamRoles && labTeamRoles.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{ 
                        background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                        boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)'
                      }}
                    >
                      <FlaskConical className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                        LAB Recommended Roles
                      </h3>
                      <p className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                        From Q3 Growth Strategy Session • {conversionDate}
                      </p>
                    </div>
                  </div>
                  <LabRecommendedBadge size="sm" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {labTeamRoles.map((role, idx) => (
                    <div 
                      key={role.id}
                      className="p-5 rounded-xl transition-all hover:shadow-lg"
                      style={{ 
                        backgroundColor: DESIGN.colors.card,
                        borderLeft: '4px solid #8B5CF6',
                        boxShadow: DESIGN.shadows.sm
                      }}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <LabIndicatorDot tooltip={false} />
                          <h4 className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                            {role.title}
                          </h4>
                        </div>
                        <span 
                          className="text-xs px-2 py-1 rounded-full font-semibold"
                          style={{ 
                            backgroundColor: '#22C55E20',
                            color: '#22C55E'
                          }}
                        >
                          {role.status}
                        </span>
                      </div>
                      
                      <p className="text-sm mb-3" style={{ color: DESIGN.colors.textSecondary }}>
                        {role.description}
                      </p>
                      
                      <div className="flex items-center gap-4 mb-3">
                        <span 
                          className="text-xs px-2 py-1 rounded"
                          style={{ 
                            backgroundColor: '#22C55E20',
                            color: '#22C55E'
                          }}
                        >
                          {role.lane} Lane
                        </span>
                        <span 
                          className="text-xs px-2 py-1 rounded"
                          style={{ 
                            backgroundColor: DESIGN.colors.background,
                            color: DESIGN.colors.textMuted
                          }}
                        >
                          {role.level}
                        </span>
                        <span className="text-xs flex items-center gap-1" style={{ color: DESIGN.colors.amber }}>
                          <AlertTriangle className="w-3 h-3" />
                          Needed by {role.deadline}
                        </span>
                      </div>
                      
                      {role.requirements && role.requirements.length > 0 && (
                        <div className="mt-3 pt-3 border-t" style={{ borderColor: DESIGN.colors.border }}>
                          <p className="text-xs font-semibold mb-2" style={{ color: DESIGN.colors.textMuted }}>
                            Requirements:
                          </p>
                          <ul className="space-y-1">
                            {role.requirements.map((req, reqIdx) => (
                              <li 
                                key={reqIdx}
                                className="text-xs flex items-center gap-2"
                                style={{ color: DESIGN.colors.textSecondary }}
                              >
                                <span className="w-1 h-1 rounded-full" style={{ backgroundColor: '#8B5CF6' }} />
                                {req}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <DirectoryTab 
              roles={roles}
              departments={departments}
              people={people}
              loading={loading}
              onUpdate={handleRoleUpdate}
              searchQuery={searchQuery}
            />
          </TabsContent>

          <TabsContent value="internal-team">
            <TeamDirectoryTab />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab onUpdate={handleRoleUpdate} />
          </TabsContent>

          <TabsContent value="expansion">
            <ExpansionSimulatorTab />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
};

export default Structure;
